(() => {
  if (window.CarsonSwiperLoader) return;

  const loaderElement = document.currentScript;
  const scriptUrl = loaderElement?.dataset.swiperScript;
  const stylesheetUrl = loaderElement?.dataset.swiperStylesheet;
  const pendingElements = new WeakMap();
  let readinessPromise;

  const loadStylesheet = () =>
    new Promise((resolve, reject) => {
      const existingLink = document.getElementById('CarsonSwiperStylesheet');

      if (existingLink) {
        if (existingLink.sheet) {
          resolve();
          return;
        }

        existingLink.addEventListener('load', resolve, { once: true });
        existingLink.addEventListener('error', reject, { once: true });
        return;
      }

      const link = document.createElement('link');
      link.id = 'CarsonSwiperStylesheet';
      link.rel = 'stylesheet';
      link.href = stylesheetUrl;
      link.addEventListener('load', resolve, { once: true });
      link.addEventListener('error', reject, { once: true });
      document.head.appendChild(link);
    });

  const loadScript = () =>
    new Promise((resolve, reject) => {
      if (window.Swiper) {
        resolve();
        return;
      }

      const existingScript = document.getElementById('CarsonSwiperScript');
      if (existingScript) {
        existingScript.addEventListener('load', resolve, { once: true });
        existingScript.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.id = 'CarsonSwiperScript';
      script.src = scriptUrl;
      script.async = true;
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });

  const load = () => {
    if (readinessPromise) return readinessPromise;

    if (!scriptUrl || !stylesheetUrl) {
      readinessPromise = Promise.reject(new Error('Swiper asset URLs are unavailable.'));
      return readinessPromise;
    }

    readinessPromise = Promise.all([loadStylesheet(), loadScript()]).then(() => {
      if (!window.Swiper) throw new Error('Swiper failed to initialize.');

      document.dispatchEvent(new CustomEvent('carson:swiper-ready'));
      return window.Swiper;
    });

    return readinessPromise;
  };

  const isNearViewport = (element) => {
    const bounds = element.getBoundingClientRect();
    return bounds.top <= window.innerHeight + 800 && bounds.bottom >= -400;
  };

  const readyFor = (element) => {
    if (!element || isNearViewport(element) || !('IntersectionObserver' in window)) {
      return load();
    }

    const existingPromise = pendingElements.get(element);
    if (existingPromise) return existingPromise;

    const elementPromise = new Promise((resolve, reject) => {
      let observer;

      const cleanup = () => {
        observer?.disconnect();
        element.removeEventListener('pointerenter', start);
        element.removeEventListener('pointerdown', start);
        element.removeEventListener('touchstart', start);
        element.removeEventListener('focusin', start);
        pendingElements.delete(element);
      };

      const start = () => {
        cleanup();
        load().then(resolve, reject);
      };

      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) start();
        },
        { rootMargin: '800px 0px' }
      );

      element.addEventListener('pointerenter', start, { once: true, passive: true });
      element.addEventListener('pointerdown', start, { once: true, passive: true });
      element.addEventListener('touchstart', start, { once: true, passive: true });
      element.addEventListener('focusin', start, { once: true });
      observer.observe(element);
    });

    pendingElements.set(element, elementPromise);
    return elementPromise;
  };

  window.CarsonSwiperLoader = {
    load,
    readyFor,
    get ready() {
      return readinessPromise;
    },
  };
})();
